import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { firebaseAuth } from '../../lib/firebase';
import { exchangeCodeForToken } from '../../lib/popup-handler';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle, ServerCrash, XCircle } from 'lucide-react';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loading, user, backendError } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const hasRun = useRef(false);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasRun.current) return;
    hasRun.current = true;

    handleCallback();
  }, []);

  // Watch for auth state changes after Firebase sign-in
  useEffect(() => {
    if (!waitingForAuth) return;

    const elapsed = Date.now() - startTimeRef.current;

    // Success - user is authenticated
    if (!loading && user) {
      setWaitingForAuth(false);
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('code_verifier');

      // Read return_url from sessionStorage (set by ProtectedRoute → oauth.ts)
      const returnUrl = sessionStorage.getItem('return_url');
      sessionStorage.removeItem('return_url');

      // Validate return_url is a relative path (security: prevent open redirect)
      const destination = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/dashboard';

      navigate(destination, { replace: true });
      return;
    }

    // Backend error detected
    if (!loading && backendError) {
      setWaitingForAuth(false);
      const errorMsg = backendError.message;
      setError(errorMsg);
      setErrorCode(backendError.statusCode);

      // Sign out from Firebase (but keep showing error page)
      signOut(firebaseAuth).catch(err => console.error('Failed to sign out:', err));
      return;
    }

    // Timeout after 10 seconds
    if (elapsed > 10000) {
      setWaitingForAuth(false);
      setError('Authentication timeout. Please try again.');
      signOut(firebaseAuth).catch(err => console.error('Failed to sign out:', err));
    }
  }, [loading, user, backendError, waitingForAuth, navigate]);

  async function handleCallback() {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        throw new Error('Missing authorization code or state');
      }

      // Validate state (CSRF protection)
      const storedState = sessionStorage.getItem('oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      // Clean URL immediately - remove sensitive OAuth parameters from address bar
      window.history.replaceState({}, '', '/signing-in');

      // Exchange code for custom token
      const { customToken } = await exchangeCodeForToken(code);

      // Sign in with Firebase
      await signInWithCustomToken(firebaseAuth, customToken);

      // Start waiting for backend sync
      // The useEffect hook will watch for state changes and handle success/error
      startTimeRef.current = Date.now();
      setWaitingForAuth(true);
    } catch (err) {
      console.error('OAuth callback error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);

      // Sign out from Firebase to prevent auth loop
      try {
        await signOut(firebaseAuth);
      } catch (signOutError) {
        console.error('Failed to sign out:', signOutError);
      }
    }
  }

  if (error) {
    const is403 = errorCode === 403;
    const is500 = errorCode === 500;

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              {is403 ? (
                <AlertTriangle className="w-16 h-16 text-yellow-500" />
              ) : is500 ? (
                <ServerCrash className="w-16 h-16 text-red-500" />
              ) : (
                <XCircle className="w-16 h-16 text-red-500" />
              )}
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${is403 ? 'text-yellow-700' : 'text-red-600'}`}>
              {is403 ? 'Account Issue' : is500 ? 'Server Error' : 'Authentication Failed'}
            </h1>
            <p className="text-gray-700 mb-6 text-sm">{error}</p>

            {is500 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6 text-sm text-left">
                <p className="font-semibold text-blue-900 mb-2">What happened?</p>
                <p className="text-blue-800">
                  There was a temporary server error while setting up your account. This is usually resolved quickly.
                </p>
              </div>
            )}

            {is403 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-6 text-sm text-left">
                <p className="font-semibold text-yellow-900 mb-2">What can I do?</p>
                <p className="text-yellow-800">
                  Please contact support for assistance with your account.
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  // Complete sign-out from all systems
                  sessionStorage.clear();
                  localStorage.clear();

                  // Redirect to OptiPrime logout which will clear SSO session and redirect back
                  const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'https://auth-dev.firebaseapp.com';
                  window.location.href = `${AUTH_URL}/logout?redirect=${encodeURIComponent(window.location.origin)}`;
                }}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
              >
                Go Home
              </button>
              {is500 && (
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Completing authentication...</p>
      </div>
    </div>
  );
}
